import { useCallback, useMemo } from 'react'

import { HStack, IconButton, Spacer, Text, VStack } from '@chakra-ui/react'
import { HideIcon, ShowIcon } from '@editor/assets/icons'
import { DeleteIcon } from '@editor/chakra-ui-icons'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { removeDrawings } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { Tools } from '@editor/pages/projects/blueprint-editor/tools/setup'
import { Layer } from '@editor/pages/projects/editor/infoPanels/components/Layer'
import { getItemsByIds } from '@editor/pages/projects/editor/infoPanels/utils'
import { isDrawing } from '@editor/services/Drawing'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setSelectedElementIds, toggleHiddenElementIds, toggleSelectedElementIds } from '@editor/store/tool'
import { difference } from 'lodash'
import { useSelector } from 'react-redux'

const BlueprintDrawings = () => {
  // Store
  const dispatch = useAppDispatch()
  const hiddenElementIds = useSelector((state: RootState) => state.toolUtil.core.hiddenElementIds)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const isBatchSaving = useSelector((state: RootState) => state.blueprint.editor.isBatchSaving)
  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)

  // Derived
  const isInvisible = useMemo(
    () => !difference(selectedElementIds, hiddenElementIds).length,
    [selectedElementIds, hiddenElementIds],
  )

  /**
   * Every selected item can be batch hidden/shown.
   */
  const canBatchVisibility = useMemo(() => selectedElementIds.length > 0, [selectedElementIds])

  // Initialize all tools
  const toolsObjectsPanel = Tools.map((tool) =>
    tool.hooks?.useDrawingsPanel ? tool.hooks.useDrawingsPanel() : undefined,
  ).filter((config) => !!config)

  /**
   * Generate panel items for each tool
   */
  const toolsPanelItems = useMemo((): EditorPanelItem[] => {
    const getItem = (item: EditorPanelItem): EditorPanelItem => ({
      ...item,
      children: (item.children?.map((child) => getItem(child)) || []).concat(item.item ? getPanelItems(item) : []),
      onDelete: isAllowedToModify ? item.onDelete : false,
      onSelect: workingElementId || editingElementId ? false : item.onSelect,
    })

    const getPanelItems = (parent?: EditorPanelItem) => {
      const parents = toolsObjectsPanel
        .map((hook) => {
          if (!hook) return undefined

          let items: EditorPanelItem[] | undefined
          if (hook?.getPanelItems) {
            items = hook.getPanelItems(parent?.item).map((item) => getItem(item))
          }

          return items
        })
        .flat()
        .filter(Boolean) as EditorPanelItem[]

      return parents
    }

    return [
      {
        key: 'drawings',
        name: '境界図形',
        isLoading: isBatchSaving,
        children: getPanelItems().sort((a, b) => {
          if (a.isWorking && !b.isWorking) return 1
          if (!a.isWorking && b.isWorking) return -1
          if (!a.item || !b.item || !isDrawing(a.item) || !isDrawing(b.item)) return -1

          // Order by order if available
          const { order: aOrder } = a.item.drawing_parameters
          const { order: bOrder } = b.item.drawing_parameters
          if (aOrder !== null && bOrder !== null) {
            return aOrder - bOrder
          }

          // Use name
          if (a.item.name && b.item.name) {
            return a.item.name.localeCompare(b.item.name)
          }

          // Last resort, compare by UUID
          return a.item.drawing_parameters.uuid.localeCompare(b.item.drawing_parameters.uuid)
        }),
        isVirtualContainer: true,
        color: '#FFFFFF',
        textStyle: { fontWeight: 'bold', fontSize: '1.2em' },
        isCountable: true,
      },
    ]
  }, [toolsObjectsPanel, isBatchSaving, isAllowedToModify, workingElementId, editingElementId])

  /**
   * Only non-working items can be batch deleted.
   */
  const canBatchDelete = useMemo(() => {
    const items = getItemsByIds(selectedElementIds, toolsPanelItems)
    const areAllWorkingItems = items.every((item) => item.isWorking)

    return selectedElementIds.length && !areAllWorkingItems
  }, [toolsPanelItems, selectedElementIds])

  /**
   * Handle batch deletion of selected elements.
   *
   * @param e - The mouse event that triggered the deletion.
   */
  const handleBatchDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      dispatch(removeDrawings(selectedElementIds))
    },
    [dispatch, selectedElementIds],
  )

  /**
   * Custom handler to toggle visibility of selected elements.
   *
   * @param e - The mouse event that triggered the visibility toggle.
   */
  const handleBatchToggleVisibility = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      dispatch(
        toggleHiddenElementIds({
          ids: selectedElementIds,
          invisible: !isInvisible,
        }),
      )
    },
    [dispatch, selectedElementIds, isInvisible],
  )

  /**
   * Custom handler to select a layer.
   *
   * @param uuid - The UUID of the drawing to select.
   */
  const handleSelectLayer = useCallback(
    (uuids: string[], selected: boolean) => {
      dispatch(toggleSelectedElementIds({ ids: uuids, selected }))
    },
    [dispatch],
  )

  return (
    <>
      {/* Batch operation */}
      <HStack
        id="drawings-panel-batch-operation"
        w="100%"
        px={2}
        py={1}
        backgroundColor={canBatchDelete ? 'blue.600' : 'gray2.800'}
        color={canBatchDelete ? 'white' : undefined}
        borderBottomColor="gray2.900"
        borderBottomWidth={1}
        cursor="pointer"
        onClick={() => {
          dispatch(setSelectedElementIds([]))
        }}
        position="sticky"
        top="33px"
        left={0}
        zIndex={10}
      >
        <Text flex={1} whiteSpace="nowrap">
          {canBatchDelete ? '選択した要素 (クリックで全選択解除)' : '選択要素なし '}
        </Text>
        <Spacer />
        <IconButton
          aria-label="hide"
          opacity={!canBatchVisibility ? 0.5 : 1}
          color={!canBatchVisibility ? undefined : 'white'}
          cursor={!canBatchVisibility ? 'not-allowed' : 'pointer'}
          variant="panel-icon"
          onClick={handleBatchToggleVisibility}
        >
          {isInvisible && canBatchVisibility ? <HideIcon /> : <ShowIcon />}
        </IconButton>
        {isAllowedToModify ? (
          <IconButton
            aria-label="delete"
            variant="panel-icon"
            disabled={!canBatchDelete || isBatchSaving}
            color={!canBatchDelete ? undefined : 'white'}
            onClick={handleBatchDelete}
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
      </HStack>

      <VStack w="100%" gap={0} pb={INFO_PANEL_PADDING - 1}>
        {/* Object list */}
        {toolsPanelItems.map((item) => (
          <Layer
            key={item.key}
            item={item}
            depth={-1}
            isParentWorking={item.hasWorkingItems}
            onSelect={handleSelectLayer}
          />
        ))}
      </VStack>

      {/* Shadow clone, dummy for drag preview */}
      {/* {draggedItem && (
        <Portal>
          <Layer key="shadow-clone" item={draggedItem} depth={1} isShadowClone />
          <Layer key="shadow-clone-group" item={draggedItem} depth={1} isShadowClone forGroup />
        </Portal>
      )} */}
    </>
  )
}

export default BlueprintDrawings
